/**
 * Countdown — 디데이 카운트다운 컴포넌트
 *
 * - 날짜/시간/분/초 표시
 * - variant로 다양한 시각 스타일 제공
 * - 완료 시 D-Day 표시 및 onComplete 콜백 호출
 */

import { useState, useEffect } from 'react'
import { differenceInSeconds } from 'date-fns'
import { cn } from '../../lib/cn.js'
import styles from './Countdown.module.css'

interface CountdownProps {
  targetDate: Date | string
  label?: string
  size?: 'sm' | 'md' | 'lg'
  /** 시각 스타일 변형 */
  variant?: 'card' | 'minimal' | 'elegant' | 'flush'
  onComplete?: () => void
  className?: string
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function calcTimeLeft(target: Date): TimeLeft | null {
  const totalSeconds = differenceInSeconds(target, new Date())
  if (totalSeconds <= 0) return null

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return { days, hours, minutes, seconds }
}

const UNITS = [
  { key: 'days', label: '일' },
  { key: 'hours', label: '시간' },
  { key: 'minutes', label: '분' },
  { key: 'seconds', label: '초' },
] as const

export function Countdown({
  targetDate,
  label,
  size = 'md',
  variant = 'card',
  onComplete,
  className,
}: CountdownProps) {
  const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() => calcTimeLeft(target))

  useEffect(() => {
    const id = setInterval(() => {
      const next = calcTimeLeft(target)
      setTimeLeft(next)
      if (!next) {
        clearInterval(id)
        onComplete?.()
      }
    }, 1000)

    return () => clearInterval(id)
  }, [target, onComplete])

  return (
    <div className={cn(styles.root, styles[size], styles[variant], className)}>
      {label && <div className={styles.label}>{label}</div>}
      {timeLeft ? (
        <div className={styles.boxes}>
          {UNITS.map(({ key, label: unitLabel }, i) => (
            <div className={styles.segment} key={key}>
              <div className={styles.box}>
                <span className={styles.number}>
                  {String(timeLeft[key]).padStart(2, '0')}
                </span>
                <span className={styles.unit}>{unitLabel}</span>
              </div>
              {i < UNITS.length - 1 && (
                <span className={styles.separator} aria-hidden="true" />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.dday}>D-Day</div>
      )}
    </div>
  )
}
